import { useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { Th } from '../../utils/api';
import { LineChart, ChartCard, ChartLoader, ChartError } from '../../components/Charts';
import { MultiFilterSelect } from '../../components/MultiFilterSelect';
import {
  APP_COLORS, ALPHA_COLORS, ALPHA_LABELS, appColor,
  FilterSelect, useThFilters,
} from './thShared';

export default function ThMonthlyView({ vertical = 'ridehailing' }) {
  const {
    filters, filtersLoaded,
    appNames, setAppNames,
    alphas, setAlphas,
    province, handleProvinceChange,
    district, handleDistrictChange,
    category, setCategory,
    availableDistricts,
  } = useThFilters(vertical);

  const params = useMemo(() => ({
    vertical,
    app_name: appNames,
    alpha:    alphas,
    province,
    district,
    category,
  }), [vertical, appNames, alphas, province, district, category]);

  const { data, loading, error } = useApi(
    () => Th.monthly(vertical, {
      app_name: appNames, alpha: alphas, province, district, category,
    }),
    params,
    filtersLoaded,
  );

  // Months sorted chronologically by 'YYYY-MM'; display uses month_name
  const months = useMemo(() => [...new Set((data || []).map(r => r.month))].sort(), [data]);
  const monthLabels = useMemo(() => {
    const nameByMonth = Object.fromEntries((data || []).map(r => [r.month, r.month_name]));
    return months.map(m => nameByMonth[m] || m);
  }, [data, months]);
  const appNamesInData = useMemo(() => [...new Set((data || []).map(r => r.app_name))].sort(), [data]);

  function buildSeries(valueKey) {
    return appNamesInData.map((app, i) => {
      const byMonth = Object.fromEntries(
        (data || []).filter(r => r.app_name === app).map(r => [r.month, r[valueKey]])
      );
      return { name: app, color: appColor(app, i), data: months.map(m => byMonth[m] ?? null) };
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userSeries    = useMemo(() => buildSeries('unique_visitors'), [data, appNamesInData, months]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sessionSeries = useMemo(() => buildSeries('total_sessions'),  [data, appNamesInData, months]);

  return (
    <div>
      <div className="filters-bar" style={{ alignItems: 'flex-start' }}>
        <FilterSelect label="Category" value={category} onChange={setCategory} options={filters.categories} />
        <MultiFilterSelect
          label="App"
          values={appNames}
          onChange={setAppNames}
          options={filters.app_names}
          chipColor={v => APP_COLORS[v] || '#6366f1'}
        />
        <MultiFilterSelect
          label="Alpha"
          values={alphas}
          onChange={setAlphas}
          options={filters.alphas}
          chipColor={v => ALPHA_COLORS[v] || '#6366f1'}
          labelMap={ALPHA_LABELS}
        />
        <FilterSelect label="Province" value={province} onChange={handleProvinceChange} options={filters.provinces} />
        <FilterSelect label="District" value={district} onChange={handleDistrictChange} options={availableDistricts} />
      </div>

      <ChartCard title="Monthly Unique Visitors — by App" sub={`${category} · all months`} style={{ marginBottom: 16 }}>
        {loading && <ChartLoader height={320} />}
        {error   && <ChartError message={error} height={320} />}
        {!loading && !error && userSeries.length > 0 && (
          <LineChart categories={monthLabels} series={userSeries} height={320} />
        )}
        {!loading && !error && userSeries.length === 0 && (
          <div className="empty-state">No data for selected filters</div>
        )}
      </ChartCard>

      <ChartCard title="Monthly Sessions — by App" sub={`${category} · all months`}>
        {loading && <ChartLoader height={320} />}
        {error   && <ChartError message={error} height={320} />}
        {!loading && !error && sessionSeries.length > 0 && (
          <LineChart categories={monthLabels} series={sessionSeries} height={320} />
        )}
        {!loading && !error && sessionSeries.length === 0 && (
          <div className="empty-state">No data for selected filters</div>
        )}
      </ChartCard>
    </div>
  );
}
